import React, { useCallback } from 'react';
import { PhoneNumberModel } from '@utmost.co/enterprise-models';
import { getElementFields, InternationalPhoneNumber, useFieldSmartFormContext } from '../../forms/utils';
import {
  InternationalPhoneNumberAtom,
  InternationalPhoneNumberValue
} from '../../atoms/InternationalPhoneNumber/InternationalPhoneNumberAtom';
import { SmartformElementData } from '../../types';
import { ReadOnly } from '../../atoms/ReadOnly/ReadOnly';
import { MatchHintSlot } from '../../fields/MatchHintSlot';

export const InternationalPhoneNumberComponent: React.FC<SmartformElementData> = (props) => {
  const [phone] = getElementFields(props).required;
  const { field, readonly, getFieldPermissions, setFieldTouched, onFieldBlur } = useFieldSmartFormContext<
    Partial<PhoneNumberModel>
  >(phone.name);
  const permissions = getFieldPermissions(phone.name);
  const setTouched = field.setTouched;
  const setValue = field.setValue;
  const fieldValue = field.value;
  const internationalValue: InternationalPhoneNumberValue = InternationalPhoneNumber.toInternationalPhoneNumberValue(
    field.value
  );

  const revalidate = useCallback(
    async (isValid: boolean) => {
      setTouched(true);
      if (internationalValue?.model && !internationalValue?.model.countryCode && isValid) {
        const newModel = new InternationalPhoneNumber(internationalValue?.model).getDetails().model;
        setValue(InternationalPhoneNumber.mergeToPhoneNumberModel(newModel, fieldValue));
      }
    },
    [setTouched, internationalValue?.model, setValue, fieldValue]
  );

  if (readonly) {
    return (
      <ReadOnly
        permissionsId={phone.name}
        infoText={phone.infoText}
        fieldControl
        label={phone.label}
        value={field.value ? new InternationalPhoneNumber(field.value).toPrettyFormat() : undefined}
        permission={permissions}
      />
    );
  }
  return (
    <MatchHintSlot name={phone.name}>
      <InternationalPhoneNumberAtom
        key={phone.name}
        dataTest={phone.name}
        value={internationalValue}
        onChange={(value) => {
          if (value?.model?.number) {
            field.setValue(InternationalPhoneNumber.mergeToPhoneNumberModel(value?.model, field.value));
          } else {
            field.setValue(null);
          }
        }}
        onBlur={() => {
          // field.setTouched() always sets touched=true regardless of its argument (its only param
          // controls shouldValidate) - use the real Formik setFieldTouched directly so blur/focus can
          // toggle touched false/true, letting consumers detect each blur as a fresh transition.
          setFieldTouched(phone.name, true, false);
          // revalidate() below also touches this field on every keystroke (to surface validation errors while
          // typing), so watchFieldKeysForBlur consumers can't rely on the touched transition above to mean a
          // real blur (see personPhoneNumber.metadata.ts's `manualBlurOnly`) - report the real blur explicitly.
          if (phone.meta?.fieldKey) {
            onFieldBlur?.(phone.meta.fieldKey, field.value, phone.name);
          }
        }}
        onFocus={() => {
          setFieldTouched(phone.name, false, false);
        }}
        onModelInit={revalidate}
        disabled={field.disabled}
        error={field.error}
        required={phone.validation.required}
        allowedCountryCodes={phone.validation.allowedCountryCodes}
        helpText={phone.helpText}
        infoText={phone.infoText}
        label={phone.label}
        placeholder={phone.hint}
        permission={permissions}
      />
    </MatchHintSlot>
  );
};
